import React from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { isEmpty } from 'lodash';
import IBAN from 'iban';

import { getVoucherInfo } from 'modules/store/actions/userActionsExt';

import { Button, TextInputRef, Spinner } from 'modules/common/components';
import { PaymentDetails, StepDots } from '.';
import { Checkbox } from 'modules/elements/components';

import { VALIDATE } from 'modules/common/constants/generic';

const doneTypingInterval = 1500;

export interface SignUpPaymentFormProps {
  onSuccess: (data) => void;
}

export default function SignUpPaymentForm({
  onSuccess,
}: SignUpPaymentFormProps) {
  const dispatch = useDispatch();
  const { t } = useTranslation(['payment', 'common']);
  const {
    register,
    getValues,
    handleSubmit,
    watch,
    setError,
    clearErrors,

    formState: { errors },
  } = useForm();

  const [isLoading] = React.useState(false);
  const [agreed, setAgreed] = React.useState(false);

  const onSubmit = () => {
    onSuccess(getValues());
  };

  const [voucherInfo, setVoucherInfo] = React.useState<
    undefined | CouponInfoDTO
  >();

  const voucher = watch('voucher');
  const [isVoucherChecking, setIsVoucherChecking] = React.useState(false);

  React.useEffect(() => {
    setVoucherInfo(undefined);
    setIsVoucherChecking(true);
    if (!voucher) {
      setIsVoucherChecking(false);
    }

    const delayDebounceFn = setTimeout(async () => {
      if (voucher) {
        const response: any = await dispatch(
          getVoucherInfo(voucher, true, { returnError: true })
        );

        setIsVoucherChecking(false);

        if (response?.percentOff) {
          setVoucherInfo(response);
        } else {
          setVoucherInfo({} as CouponInfoDTO);
        }
      }
    }, doneTypingInterval);

    return () => clearTimeout(delayDebounceFn);
  }, [voucher]);

  const handleAgreementCheckboxClick = () => {
    setAgreed((prev) => !prev);
  };

  const isCodeInvalid = !!voucherInfo && isEmpty(voucherInfo);
  const isFree = voucherInfo?.percentOff === 100;
  const voucherInputLabel = t('payment:step_sepa_coupon_name');
  const voucherError = isCodeInvalid ? t('auth:coupon_invalid') : '';
  const validVoucherMessage = !isEmpty(voucherInfo || {})
    ? t('auth:coupon_valid')
    : '';
  const errorIban =
    errors.iban?.type === VALIDATE
      ? t('auth:invalid_iban')
      : `${t('auth:iban_label')} ${t('common:required')}`;

  React.useEffect(() => {
    if (voucherError) {
      setError('voucher', { type: 'invalid', message: voucherError });
    } else {
      clearErrors('voucher');
    }
  }, [voucherError]);

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="t-mt-5">
      <div className="t-my-5">
        <TextInputRef
          {...register('voucher', {
            validate: () => {
              if (voucherError) return voucherError;
              return true;
            },
          })}
          label={voucherInputLabel}
          type="text"
          autoComplete={false}
          error={voucherError}
          wrapperClassName="t-pb-0"
          iconRight={
            isVoucherChecking ? (
              <Spinner className="t-w-4 t-text-alpha-600" />
            ) : null
          }
        />
        {validVoucherMessage && (
          <p className="typo-epsilon t-text-alpha-600 t-mt-1">
            {validVoucherMessage}
          </p>
        )}
      </div>

      {!isFree && (
        <div>
          <TextInputRef
            {...register('accountHolderName', { required: true })}
            label={t('auth:accountholder_name')}
            placeholder={t('auth:accountholder_name')}
            type="text"
            autoComplete={false}
            error={
              errors.accountHolderName &&
              `${t('auth:accountholder_name')} ${t('common:required')}`
            }
          />

          <TextInputRef
            {...register('iban', {
              required: true,
              validate: (value) => {
                if (!IBAN.isValid(value)) {
                  return false;
                }
                return true;
              },
            })}
            label={t('auth:iban_label')}
            placeholder={t('auth:iban_placeholder')}
            type="text"
            autoComplete={false}
            error={errors.iban && errorIban}
            mask="**99 9999 9999 9999 9999 99"
          />
        </div>
      )}

      <PaymentDetails voucherInfo={voucherInfo} />

      <p
        className="typo-theta t-text-beta-700 t-mt-5"
        style={{ fontSize: '8px' }}
      >
        {t('auth:payment_info_note')}
      </p>

      {!isFree && (
        <div className="t-mt-5 t-flex t-items-start">
          <Checkbox
            checked={agreed}
            id="agreed"
            onChange={handleAgreementCheckboxClick}
            isBlueBg
          />
          <p
            className="typo-theta t-text-beta-700 t-ml-2"
            style={{ fontSize: '8px' }}
          >
            {t('auth:agreement_checkbox_text')}
          </p>
        </div>
      )}

      <div className="t-flex t-justify-between t-items-center t-mt-5">
        <StepDots step={2} total={3} />

        <Button
          type="submit"
          className=""
          loading={isLoading}
          disabled={!isFree && !agreed}
        >
          {t('auth:continue')}
        </Button>
      </div>
    </form>
  );
}
